import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useReferralId } from 'hooks/useReferralId';
import { useReview } from 'hooks/useReview';
import Referral from 'models/reviews/Referral';
import { referralQueryKeys, referralQueryParams } from 'services/api/reviews';

type ReferralContextType = {
  referral?: Referral;
  referralLoading: boolean;
  setSelectedReferralId: (referralId: string) => void;
};

const ReferralContext = createContext<ReferralContextType>({
  referral: undefined,
  referralLoading: true,
  setSelectedReferralId: () => {},
});

export function ReferralProvider({ children }: { children: React.ReactNode }) {
  const referralId = useReferralId();
  const [selectedReferralId, setSelectedReferralId] = useState<string>(referralId ?? '');

  useEffect(() => {
    if (referralId) {
      setSelectedReferralId(referralId);
    }
  }, [referralId, setSelectedReferralId]);

  const { review, loading } = useReview(selectedReferralId, referralQueryKeys, referralQueryParams);

  const value = useMemo(
    () => ({
      referral: review as Referral,
      referralLoading: loading,
      setSelectedReferralId,
    }),
    [review, loading]
  );

  return <ReferralContext value={value}>{children}</ReferralContext>;
}

export function useReferral() {
  const context = useContext(ReferralContext);

  if (!context) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
}
